import { useCallback, useMemo } from "react";
import { generatePath, useLocation } from "react-router-dom";

import { useAtomValue } from "jotai";

import { AbsoluteRoute } from "../../core/routes/absoluteRoutes";
import { bridgeAtom } from "../bridge/bridgeAtom";
import { isEmbedPath } from "../isEmbedPath";

export interface RemoteAssetsRoutes {
    assetList(orgId: string): string;
    assetDetail(orgId: string, remoteAssetId: string): string;
    lensDetail(lensId: string): string | null;
    openExternal(url: string): void;
}

export const useRemoteAssetsRoutes = (): RemoteAssetsRoutes => {
    const location = useLocation();
    const isEmbed = isEmbedPath(location.pathname);
    const bridgeState = useAtomValue(bridgeAtom);

    const assetList = useCallback(
        (orgId: string) =>
            generatePath(isEmbed ? AbsoluteRoute.EMBED_REMOTE_ASSETS : AbsoluteRoute.ORG_REMOTE_ASSETS, { orgId }),
        [isEmbed],
    );

    const assetDetail = useCallback(
        (orgId: string, remoteAssetId: string) =>
            generatePath(isEmbed ? AbsoluteRoute.EMBED_REMOTE_ASSET_DETAIL : AbsoluteRoute.ORG_REMOTE_ASSET_DETAIL, {
                orgId,
                remoteAssetId,
            }),
        [isEmbed],
    );

    const lensDetail = useCallback(
        (lensId: string): string | null => {
            if (isEmbed) return null;
            return generatePath(AbsoluteRoute.LENS, { lensId });
        },
        [isEmbed],
    );

    const openExternal = useCallback(
        (url: string) => {
            if (bridgeState.bridge) {
                bridgeState.bridge.openExternalUrl(url);
            } else {
                window.open(url, "_blank");
            }
        },
        [bridgeState.bridge],
    );

    return useMemo(
        () => ({ assetList, assetDetail, lensDetail, openExternal }),
        [assetList, assetDetail, lensDetail, openExternal],
    );
};
